<script>
import moment from 'moment'
import { formatCurrencyBRL, getReceivableAmounts, parseCurrencyBRL } from '@/helpers/accountsReceivableStatus'

export default {
  name: 'PartialReceiveModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    conta: {
      type: Object,
      default: null
    },
    planoContas: {
      type: Array,
      default: () => []
    },
    busy: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: this.buildInitialForm(),
      errors: {}
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    amounts() {
      return getReceivableAmounts(this.conta || {})
    },
    saldoAposBaixa() {
      return Math.max(this.amounts.saldo - this.valorAtual, 0)
    },
    valorAtual() {
      return parseCurrencyBRL(this.form.valor)
    },
    contaCreditoOptions() {
      return this.planoContas
        .filter((plano) => plano.conta_pgto === 0 && plano.grupo_pgto !== null)
        .map((plano) => ({ text: plano.descricao, value: plano.id }))
    },
    previewMessage() {
      if (!this.form.valor || this.errors.valor) {
        return 'Informe um valor válido para visualizar o impacto na baixa.'
      }

      const saldoTexto = formatCurrencyBRL(this.saldoAposBaixa)
      const valorTexto = formatCurrencyBRL(this.valorAtual)
      const saldoAtualTexto = formatCurrencyBRL(this.amounts.saldo)

      if (this.saldoAposBaixa === 0) {
        return `Você está registrando ${valorTexto} em ${this.dataTexto}. O saldo passará de ${saldoAtualTexto} para ${saldoTexto}. O título será marcado como Quitado.`
      }

      return `Você está registrando ${valorTexto} em ${this.dataTexto}. O saldo passará de ${saldoAtualTexto} para ${saldoTexto}.`
    },
    dataTexto() {
      return this.form.data_recebimento ? moment(this.form.data_recebimento).format('DD/MM/YYYY') : '-'
    },
    hasConta() {
      return !!this.conta
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          this.resetForm()
        }
      }
    },
    conta: {
      immediate: true,
      deep: true,
      handler() {
        if (this.value) {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    buildInitialForm() {
      return {
        id: null,
        valor: '',
        data_recebimento: moment().format('YYYY-MM-DD'),
        conta_credito_id: null,
        observacao: ''
      }
    },
    resetForm() {
      this.form = {
        ...this.buildInitialForm(),
        id: this.conta?.id ?? null,
        conta_credito_id: this.conta?.conta_credito_id ?? null
      }
      this.errors = {}
    },
    close() {
      this.$emit('input', false)
      this.$emit('close')
    },
    formatCurrency(value) {
      return formatCurrencyBRL(value)
    },
    normalizeInputValue(value) {
      this.form.valor = value
      this.validateValor(false)
    },
    formatValorOnBlur() {
      const parsed = parseCurrencyBRL(this.form.valor)
      if (parsed > 0) {
        this.form.valor = parsed.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }
      this.validateValor(true)
    },
    setFullBalance() {
      this.form.valor = this.amounts.saldo.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      this.validateValor(true)
    },
    validateValor(showMessage = true) {
      const parsed = parseCurrencyBRL(this.form.valor)
      if (!parsed || parsed <= 0) {
        if (showMessage) {
          this.errors.valor = 'Informe um valor maior que zero.'
        }
        return false
      }

      if (parsed > this.amounts.saldo) {
        if (showMessage) {
          this.errors.valor = 'O valor informado não pode ser maior que o saldo restante.'
        }
        return false
      }

      delete this.errors.valor
      return true
    },
    validateForm() {
      const validValor = this.validateValor(true)

      if (!this.form.data_recebimento) {
        this.errors.data_recebimento = 'Data de recebimento é obrigatória.'
      } else {
        delete this.errors.data_recebimento
      }

      if (!this.form.conta_credito_id) {
        this.errors.conta_credito_id = 'Conta de recebimento é obrigatória.'
      } else {
        delete this.errors.conta_credito_id
      }

      if (!this.form.observacao) {
        delete this.errors.observacao
      }

      return validValor && !this.errors.data_recebimento && !this.errors.conta_credito_id
    },
    submit() {
      if (!this.validateForm()) {
        return
      }

      this.$emit('confirm', {
        id: this.conta?.id ?? this.form.id,
        valor_recebido: parseCurrencyBRL(this.form.valor),
        data_recebimento: this.form.data_recebimento,
        conta_credito_id: this.form.conta_credito_id,
        observacao: this.form.observacao || null
      })
    }
  }
}
</script>

<template>
  <b-modal
    v-model="isVisible"
    :title="`Registrar baixa - Doc. ${conta?.numero_documento || conta?.id || '-'}`"
    size="lg"
    centered
    hide-header-close
    no-close-on-backdrop
    @hidden="close"
  >
    <div v-if="hasConta">
      <div class="card border-0 mb-3 bg-light">
        <div class="card-body">
          <div class="row">
            <div class="col-md-3 col-6 mb-2">
              <small class="text-muted d-block">Valor original</small>
              <strong>{{ formatCurrency(amounts.original) }}</strong>
            </div>
            <div class="col-md-3 col-6 mb-2">
              <small class="text-muted d-block">Já recebido</small>
              <strong>{{ formatCurrency(amounts.received) }}</strong>
            </div>
            <div class="col-md-3 col-6 mb-2">
              <small class="text-muted d-block">Saldo restante</small>
              <strong class="text-danger">{{ formatCurrency(amounts.saldo) }}</strong>
            </div>
            <div class="col-md-3 col-6 mb-2">
              <small class="text-muted d-block">Saldo após baixa</small>
              <strong :class="saldoAposBaixa === 0 ? 'text-success' : 'text-primary'">
                {{ formatCurrency(saldoAposBaixa) }}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group label="Valor da baixa *" label-for="partial-receive-valor">
            <b-input-group>
              <b-form-input
                id="partial-receive-valor"
                v-model="form.valor"
                type="text"
                inputmode="decimal"
                :state="errors.valor ? false : null"
                aria-describedby="partial-receive-valor-feedback"
                @input="normalizeInputValue"
                @blur="formatValorOnBlur"
              />
              <b-input-group-append>
                <b-button variant="outline-primary" size="sm" @click="setFullBalance">
                  Quitar saldo
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small id="partial-receive-valor-feedback" class="text-danger" v-if="errors.valor">
              {{ errors.valor }}
            </small>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label="Data do recebimento *" label-for="partial-receive-data">
            <b-form-input
              id="partial-receive-data"
              v-model="form.data_recebimento"
              type="date"
              :state="errors.data_recebimento ? false : null"
            />
            <small class="text-danger" v-if="errors.data_recebimento">
              {{ errors.data_recebimento }}
            </small>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group label="Conta de recebimento *" label-for="partial-receive-conta">
            <b-form-select
              id="partial-receive-conta"
              v-model="form.conta_credito_id"
              :options="contaCreditoOptions"
              :state="errors.conta_credito_id ? false : null"
            />
            <small class="text-danger" v-if="errors.conta_credito_id">
              {{ errors.conta_credito_id }}
            </small>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label="Observação" label-for="partial-receive-observacao">
            <b-form-textarea
              id="partial-receive-observacao"
              v-model="form.observacao"
              rows="3"
              max-rows="5"
              placeholder="Opcional"
            />
          </b-form-group>
        </div>
      </div>

      <div class="alert alert-info mb-0">
        {{ previewMessage }}
      </div>
    </div>
    <div v-else class="text-muted">
      Nenhuma conta selecionada.
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="close">Cancelar</b-button>
      <b-button variant="success" :disabled="busy || !hasConta" @click="submit">
        <b-spinner v-if="busy" small class="mr-2" />
        Confirmar baixa
      </b-button>
    </template>
  </b-modal>
</template>
